import { Controller } from "@hotwired/stimulus"

// Polling fallback for the employee CSV import show page. The page normally
// leaves the "processing" view via a navigation after the import job finishes,
// but a fast job can complete before the user's page settles. While the import
// is in a transient state this controller polls the show page (Turbo Stream)
// passing its current status; the server replaces the panel only when the
// status changed (otherwise 204). The controller lives inside the replaced
// panel, so each swap re-instantiates it with the fresh status.
const TRANSIENT_STATES = ["pending", "analyzing", "processing"]

export default class extends Controller {
  static values = {
    url: String,
    status: String,
    interval: { type: Number, default: 2500 },
  }

  connect() {
    if (!TRANSIENT_STATES.includes(this.statusValue)) return

    this.boundVisibilityChange = this.handleVisibilityChange.bind(this)
    document.addEventListener("visibilitychange", this.boundVisibilityChange)
    this.scheduleTick()
  }

  disconnect() {
    this.clearTimer()
    if (this.boundVisibilityChange) {
      document.removeEventListener("visibilitychange", this.boundVisibilityChange)
    }
  }

  scheduleTick() {
    this.clearTimer()
    if (document.hidden) return

    this.timer = setTimeout(() => this.poll(), this.intervalValue)
  }

  clearTimer() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = null
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.clearTimer()
    } else {
      this.poll()
    }
  }

  async poll() {
    let body = ""
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("known_status", this.statusValue)
      const response = await fetch(url, {
        headers: { Accept: "text/vnd.turbo-stream.html" },
      })
      if (response.ok && response.status !== 204) {
        body = await response.text()
      }
    } catch {
      // Network hiccups are fine — re-arm and the next tick retries.
    }

    if (body) {
      Turbo.renderStreamMessage(body)
    } else {
      this.scheduleTick()
    }
  }
}
